import { useState, useRef, useEffect } from 'react';
import { useGetAudioPlaybackUrl } from '@/queries/articles';
import PlayIcon from '@/assets/common/play.svg?react';
import PauseIcon from '@/assets/common/pause.svg?react';
import VolumeIcon from '@/assets/common/volume.svg?react';
import VolumeMuteIcon from '@/assets/common/volume-mute.svg?react';
import styles from './AudioPlayer.module.scss';

interface AudioPlayerProps {
  articleId: string;
  recordingId: string;
  title: string;
  durationSeconds?: number | null;
}

function formatTime(seconds: number): string {
  if (!seconds || seconds <= 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function AudioPlayer({
  articleId,
  recordingId,
  title,
  durationSeconds,
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationSeconds || 0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getPlaybackUrl = useGetAudioPlaybackUrl();

  // Load audio URL on first play
  const loadAudio = async () => {
    if (audioUrl) return; // Already loaded

    setIsLoading(true);
    setError(null);

    try {
      const response = await getPlaybackUrl.mutateAsync({ articleId, recordingId });
      setAudioUrl(response.playbackUrl);
    } catch (err) {
      setError('Failed to load audio');
      console.error('Error loading audio:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Play/pause toggle
  const handlePlayPause = async () => {
    if (!audioUrl) {
      await loadAudio();
      return;
    }

    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  };

  // Seek on progress bar click
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    const progressBar = progressRef.current;
    if (!audio || !progressBar || !audioUrl) return;

    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    audio.currentTime = percentage * duration;
  };

  // Volume toggle
  const handleVolumeToggle = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      audio.muted = false;
      setIsMuted(false);
    } else {
      audio.muted = true;
      setIsMuted(true);
    }
  };

  // Volume change
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newVolume = parseFloat(e.target.value);
    audio.volume = newVolume;
    setVolume(newVolume);
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  };

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration || durationSeconds || 0);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const handleError = () => setError('Error playing audio');

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    // Auto-play once loaded
    audio.play();

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [audioUrl, durationSeconds]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={styles.player}>
      {audioUrl && <audio ref={audioRef} src={audioUrl} preload="auto" />}

      <button
        className={styles.player__playButton}
        onClick={handlePlayPause}
        disabled={isLoading}
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isLoading ? (
          <span className={styles.player__spinner} />
        ) : isPlaying ? (
          <PauseIcon />
        ) : (
          <PlayIcon />
        )}
      </button>

      <div className={styles.player__content}>
        <span className={styles.player__title}>{title}</span>

        <div className={styles.player__controls}>
          <div
            ref={progressRef}
            className={styles.player__progress}
            onClick={handleProgressClick}
            role="slider"
            aria-label="Audio progress"
            aria-valuemin={0}
            aria-valuemax={duration}
            aria-valuenow={currentTime}
          >
            <div
              className={styles.player__progressFill}
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className={styles.player__time}>
            <span>{formatTime(currentTime)}</span>
            <span>/</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {error && <span className={styles.player__error}>{error}</span>}
      </div>

      <div className={styles.player__volume}>
        <button
          className={styles.player__volumeButton}
          onClick={handleVolumeToggle}
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted || volume === 0 ? <VolumeMuteIcon /> : <VolumeIcon />}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={isMuted ? 0 : volume}
          onChange={handleVolumeChange}
          className={styles.player__volumeSlider}
          aria-label="Volume"
        />
      </div>
    </div>
  );
}
